import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/reactApi/api';

class ApprovalRequestApiService {
  constructor() {
    this.baseURL = `${API_BASE_URL}/accounts`;
  }

  // Create axios instance with default config
  createApiInstance() {
    const instance = axios.create({
      baseURL: this.baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    instance.interceptors.request.use((config) => {
      const token = localStorage.getItem('authToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
    return instance;
  }

  // Approval Request API calls
  async getApprovalRequests(filters = {}) {
    try {
      const api = this.createApiInstance();
      const response = await api.get('/approval-requests', { params: filters });
      return response.data;
    } catch (error) {
      console.error('Error fetching approval requests:', error);
      throw this.handleError(error);
    }
  }

  async exportApprovalRequests(filters = {}) {
    try {
      const api = this.createApiInstance();
      const response = await api.get('/approval-requests/export', { 
        params: filters,
        responseType: 'blob'
      });
      
      // Generate meaningful filename
      const currentDate = new Date().toISOString().split('T')[0];
      const statusText = filters.transactionStatus === '1' ? 'Processed' : 'UnProcessed';
      const filename = `Approval_Requests_${statusText}_${currentDate}.xlsx`;
      
      // Create download link
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return {
        success: true,
        message: `File "${filename}" downloaded successfully`,
        fileName: filename
      };
    } catch (error) {
      console.error('Error exporting approval requests:', error);
      return {
        success: false,
        message: 'Failed to export data. Please try again.'
      };
    }
  }

  async getApprovalRequestDetails(appId) {
    try {
      const api = this.createApiInstance();
      const response = await api.get(`/approval-requests/${appId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching approval request details:', error);
      throw this.handleError(error);
    }
  }

  // Master data API calls
  async getPaymentTypes() {
    try {
      const api = this.createApiInstance();
      const response = await api.get('/payment-types');
      return response.data;
    } catch (error) {
      console.error('Error fetching payment types:', error);
      throw this.handleError(error);
    }
  }

  // Encryption/Decryption utilities for URL parameters
  encryptParameters(params) {
    try {
      // Simple base64 encoding for now - in production, use proper encryption
      const encrypted = {
        id: btoa(params.id.toString()),
        type: btoa(params.type || 'Main')
      };
      return encrypted;
    } catch (error) {
      console.error('Error encrypting parameters:', error);
      return { id: '', type: '' };
    }
  }

  async decryptParameters(encryptedParams) {
    try {
      // Simple base64 decoding for now - in production, use proper decryption
      const decrypted = {
        appid: atob(encryptedParams.id),
        type: atob(encryptedParams.type)
      };
      return {
        success: true,
        data: decrypted
      };
    } catch (error) {
      console.error('Error decrypting parameters:', error);
      return {
        success: false,
        message: 'Invalid parameters'
      };
    }
  }

  // Assessor allotment data
  async getAssessorAllotment(appId, mode) {
    try {
      const api = this.createApiInstance();
      const response = await api.get(`/assessor-allotment/${appId}`, {
        params: { mode }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching assessor allotment:', error);
      throw this.handleError(error);
    }
  }

  // QMS assessors data
  async getQMSAssessors(appId, mode) {
    try {
      const api = this.createApiInstance();
      const response = await api.get(`/qms-assessors/${appId}`, {
        params: { mode }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching QMS assessors:', error);
      throw this.handleError(error);
    }
  }

  // Admin data
  async getAdminData(appId, mode) {
    try {
      const api = this.createApiInstance();
      const response = await api.get(`/admin-data/${appId}`, {
        params: { mode }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching admin data:', error);
      throw this.handleError(error);
    }
  }

  // Honorarium data
  async getHonorarium(appId, assessmentType) {
    try {
      const api = this.createApiInstance();
      const response = await api.get(`/honorarium/${appId}`, {
        params: { assessmentType }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching honorarium:', error);
      throw this.handleError(error);
    }
  }

  // CEO remarks
  async getCeoRemarks(appId, assessmentType) {
    try {
      const api = this.createApiInstance();
      const response = await api.get(`/ceo-remarks/${appId}`, {
        params: { assessmentType }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching CEO remarks:', error);
      throw this.handleError(error);
    }
  }

  // Submit assessor amount
  async submitAssessorAmount(data) {
    try {
      const api = this.createApiInstance();
      const response = await api.post('/submit-assessor-amount', data);
      return response.data;
    } catch (error) {
      console.error('Error submitting assessor amount:', error);
      throw this.handleError(error);
    }
  }

  // Send to CEO
  async sendToCEO(data) {
    try {
      const api = this.createApiInstance();
      const response = await api.post('/send-to-ceo', data);
      return response.data;
    } catch (error) {
      console.error('Error sending to CEO:', error);
      throw this.handleError(error);
    }
  }

  // Error handling utility
  handleError(error) {
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      return {
        success: false,
        message: data?.message || data?.error || `Server error: ${status}`,
        status,
        data: data?.data || null
      };
    } else if (error.request) {
      // Request was made but no response received
      return {
        success: false,
        message: 'Network error - please check your connection',
        status: 0,
        data: null
      };
    } else {
      // Something else happened
      return {
        success: false,
        message: error.message || 'An unexpected error occurred',
        status: 0,
        data: null
      };
    }
  }

  // Lab types
  getLabTypes() {
    return [
      { id: '1', name: 'Testing Laboratory' },
      { id: '2', name: 'Calibration Laboratory' },
      { id: '3', name: 'Medical Laboratory' }
    ];
  }
}

// Create and export singleton instance
const approvalRequestApiService = new ApprovalRequestApiService();
export default approvalRequestApiService;